import { createClient } from "@supabase/supabase-js";
import { withPerformanceTracking } from "./performanceMonitoring";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

export const supabase = createClient(supabaseUrl, supabaseKey);

export interface CryptoPriceData {
  id: number;
  symbol: string;
  timestamp: string;
  price: number;
  volume_24h: number | null;
  percent_change_24h: number | null;
  market_cap?: number | null;
  data_source: string;
  created_at: string;
}

export interface HealthCheckResult {
  status: string;
  lastDataPoint: string | null;
  timestamp: string;
  connected: boolean;
  error?: string;
}

// Data collection status types (for admin dashboard)
export interface DataCollectionStatus {
  isRunning: boolean;
  lastRun: string | null;
  nextRun: string | null;
  errorCount: number;
  successCount: number;
  // Legacy properties for admin dashboard compatibility
  fetch_status: 'active' | 'inactive' | 'error' | null;
  last_fetch_time: string | null;
  next_scheduled_fetch: string | null;
  error_message: string | null;
}

export interface CollectionConfig {
  interval: number;
  enabled: boolean;
  symbols: string[];
  // Legacy properties for admin dashboard compatibility
  symbol: string;
  fetch_interval_minutes: number;
  api_provider: string;
}

/**
 * Get data collection status (stub for admin dashboard)
 * This functionality has been moved to Edge Functions
 */
export async function getDataCollectionStatus(_symbol: string): Promise<DataCollectionStatus | null> {
  // Data collection is now handled by Edge Functions
  // Return null to indicate status is not available from client
  return null;
}

/**
 * Get collection config (stub for admin dashboard)
 * This functionality has been moved to Edge Functions
 */
export async function getCollectionConfig(_symbol: string): Promise<CollectionConfig | null> {
  // Collection config is now managed server-side
  // Return null to indicate config is not available from client
  return null;
}

/**
 * Fetch cryptocurrency data from Supabase for a given time range
 * Uses Edge Function (get-chart-data) instead of direct database access
 */
export async function fetchCryptoData(
  symbol: string = "XRP",
  timeRange: string = "24H",
): Promise<CryptoPriceData[]> {
  return withPerformanceTracking(
    'supabase_fetch_crypto_data',
    async () => {
      // Input validation
      if (!symbol || typeof symbol !== "string") {
        throw new Error("Symbol is required and must be a string");
      }

      // Validate symbol format (3-10 uppercase letters)
      if (!/^[A-Z]{3,10}$/.test(symbol)) {
        throw new Error(
          `Invalid symbol format: ${symbol}. Must be 3-10 uppercase letters.`,
        );
      }

      // Validate time range
      const validTimeRanges = ["24H", "48H", "7D", "30D"];
      if (!validTimeRanges.includes(timeRange)) {
        throw new Error(
          `Invalid time range: ${timeRange}. Must be one of: ${validTimeRanges.join(", ")}`,
        );
      }

      // Call Edge Function instead of direct database query
      const { data, error } = await supabase.functions.invoke('get-chart-data', {
        body: { symbol, timeRange }
      });

      if (error) {
        throw new Error(`Failed to fetch chart data: ${error.message}`);
      }

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch chart data');
      }

      return data.data || [];
    },
    { symbol, timeRange }
  );
}

/**
 * Get the latest price for a cryptocurrency
 */
export async function getLatestPrice(
  symbol: string = "XRP",
): Promise<CryptoPriceData | null> {
  return withPerformanceTracking(
    'supabase_get_latest_price',
    async () => {
      // Input validation
      if (!symbol || typeof symbol !== "string") {
        throw new Error("Symbol is required and must be a string");
      }

      if (!/^[A-Z]{3,10}$/.test(symbol)) {
        throw new Error(
          `Invalid symbol format: ${symbol}. Must be 3-10 uppercase letters.`,
        );
      }

      try {
        const { data, error } = await supabase
          .from("cryptocurrency_data")
          .select("*")
          .eq("symbol", symbol)
          .order("timestamp", { ascending: false })
          .limit(1)
          .single();

        if (error) {
          // Handle "no rows" error gracefully
          if (error.code === "PGRST116") {
            return null;
          }
          throw new Error(`Database error fetching latest price: ${error.message}`);
        }

        return data;
      } catch (error) {
        if (error instanceof Error) {
          throw error;
        }
        throw new Error("Unknown error occurred while fetching latest price");
      }
    },
    { symbol }
  );
}

/**
 * Subscribe to real-time updates for cryptocurrency data
 */
export function subscribeToCryptoData(
  symbol: string,
  callback: (payload: any) => void,
) {
  return supabase
    .channel("crypto_data_changes")
    .on(
      "postgres_changes",
      {
        event: "INSERT",
        schema: "public",
        table: "cryptocurrency_data",
        filter: `symbol=eq.${symbol}`,
      },
      callback,
    )
    .subscribe();
}

/**
 * Health check to verify Supabase connection and data freshness
 */
export async function healthCheck(symbol: string = "XRP"): Promise<HealthCheckResult> {
  return withPerformanceTracking(
    'supabase_health_check',
    async () => {
      try {
        // Check if we can connect to Supabase
        const { data: recent, error } = await supabase
          .from("cryptocurrency_data")
          .select("timestamp")
          .eq("symbol", symbol)
          .order("timestamp", { ascending: false })
          .limit(1)
          .single();

        if (error && error.code !== "PGRST116") {
          // PGRST116 is "no rows returned"
          throw error;
        }

        const isHealthy =
          recent &&
          Date.now() - new Date(recent.timestamp).getTime() < 30 * 60 * 1000; // 30 minutes

        return {
          status: isHealthy ? "healthy" : "stale_data",
          lastDataPoint: recent?.timestamp || null,
          timestamp: new Date().toISOString(),
          connected: true,
        };
      } catch (error) {
        return {
          status: "error",
          lastDataPoint: null,
          timestamp: new Date().toISOString(),
          connected: false,
          error: error instanceof Error ? error.message : "Unknown error",
        };
      }
    },
    { symbol }
  );
}

// ============================================
// User Transaction Types
// ============================================

export interface UserTransaction {
  id: string;
  wallet_address: string;
  tx_hash: string;
  tx_type: 'swap' | 'mint' | 'redeem';
  sell_token_symbol?: string;
  sell_token_address?: string;
  sell_amount?: number;
  buy_token_symbol?: string;
  buy_token_address?: string;
  buy_amount?: number;
  value_usd?: number;
  status: 'pending' | 'success' | 'failed';
  created_at: string;
  updated_at: string;
}

// ============================================
// User Transaction Functions
// ============================================

/**
 * Fetch user transactions from the database.
 * Uses direct fetch() to avoid iOS Safari hang with supabase.functions.invoke().
 */
export async function fetchUserTransactions(
  walletAddress: string,
  limit: number = 20,
  offset: number = 0
): Promise<{ data: UserTransaction[]; total: number }> {
  return withPerformanceTracking(
    'supabase_fetch_user_transactions',
    async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 8_000);

        const response = await fetch(
          `${supabaseUrl}/functions/v1/user-transactions`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${supabaseKey}`,
              'apikey': supabaseKey,
            },
            body: JSON.stringify({
              action: 'get',
              wallet_address: walletAddress.toLowerCase(),
              limit,
              offset,
            }),
            signal: controller.signal,
          }
        );
        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        return {
          data: data?.data || [],
          total: data?.total || 0,
        };
      } catch (error) {
        throw error;
      }
    },
    { wallet_address: walletAddress }
  );
}

/**
 * Update transaction status.
 * Uses direct fetch() to avoid iOS Safari hang with supabase.functions.invoke().
 */
export async function updateTransactionStatus(
  txHash: string,
  status: 'pending' | 'success' | 'failed'
): Promise<UserTransaction | null> {
  return withPerformanceTracking(
    'supabase_update_transaction_status',
    async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 8_000);

        const response = await fetch(
          `${supabaseUrl}/functions/v1/user-transactions`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${supabaseKey}`,
              'apikey': supabaseKey,
            },
            body: JSON.stringify({
              action: 'update',
              tx_hash: txHash,
              status,
            }),
            signal: controller.signal,
          }
        );
        clearTimeout(timeoutId);

        if (!response.ok) {
          return null;
        }

        const data = await response.json();
        return data?.data || null;
      } catch {
        return null;
      }
    },
    { tx_hash: txHash, status }
  );
}

/**
 * Triggers data collection if database data is stale (>30 minutes old)
 * Non-blocking - runs in background without waiting for completion
 */
export async function triggerDataCollectionIfNeeded(
  symbol: string = "XRP"
): Promise<void> {
  try {
    // Get latest data timestamp from database
    const { data: latest, error } = await supabase
      .from("cryptocurrency_data")
      .select("timestamp")
      .eq("symbol", symbol)
      .order("timestamp", { ascending: false })
      .limit(1)
      .single();

    if (error && error.code !== "PGRST116") {
      // PGRST116 = no rows, which is fine (triggers collection)
      throw error;
    }

    // Calculate data age
    const now = Date.now();
    const thirtyMinutesAgo = now - (30 * 60 * 1000);
    const latestTimestamp = latest ? new Date(latest.timestamp).getTime() : 0;

    // Trigger collection if data is stale or missing
    if (latestTimestamp < thirtyMinutesAgo) {
      // Fire and forget - direct fetch to avoid iOS Safari hang
      fetch(`${supabaseUrl}/functions/v1/collect-crypto-data`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${supabaseKey}`,
          'apikey': supabaseKey,
        },
      }).catch(() => {});
    }
  } catch (error) {
    // Silent failure - don't break the UI
  }
}












